/**
 * Etiquetas y cálculos de presentación del módulo RIC.
 */

import { cn, toNumber } from '@/lib/utils';

import type { RicFilaDia, TomaRic } from '../ric.types';

export const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
] as const;

/** Años ofrecidos en el selector de período (el panel mostraba una lista fija). */
export const ANIOS = Array.from({ length: 7 }, (_, indice) => new Date().getFullYear() - 5 + indice);

/** Color del texto de la diferencia según el umbral configurado. */
export function tonoDiferencia(diferencia: number, umbral: number): string {
  const valor = Math.abs(toNumber(diferencia));
  const limite = umbral > 0 ? umbral : 100;

  if (valor <= limite * 0.25) return 'text-ink-700';
  if (valor <= limite) return 'text-amber-600';

  return 'text-rose-600 font-semibold';
}

/** Clase del fondo de la fila cuando la diferencia supera el umbral. */
export function fondoFila(diferencia: number, umbral: number): string {
  const limite = umbral > 0 ? umbral : 100;

  return Math.abs(toNumber(diferencia)) > limite ? 'bg-rose-50/60' : '';
}

/** ¿La toma está anulada? */
export function estaAnulada(toma: Pick<TomaRic, 'ric_state'>): boolean {
  return Number(toma.ric_state) !== 1;
}

/**
 * Fórmula del RIC para una fila diaria:
 * `lógico = previo + ingresos − salidas` y `diferencia = (físico − agua) − lógico`.
 */
export function calcularFila(fila: RicFilaDia) {
  const logica = toNumber(fila.previous) + toNumber(fila.incomes) - toNumber(fila.outcomes);
  const sinAgua = toNumber(fila.physical) - toNumber(fila.water_gl);

  return { logica, sinAgua, diferencia: sinAgua - logica };
}

/**
 * Alerta del reporte de Osinerming (la calculaba el JavaScript del panel).
 *
 * Reglas heredadas, en este orden:
 * - ventas menores a 3.500 gal o desvío mayor al 40 % → `CHEQUEAR!`
 * - desvío mayor al 25 % → `AYUDA`
 * - en cualquier otro caso → `ALARMA`
 */
export type AlertaOsinerming = 'CHEQUEAR!' | 'AYUDA' | 'ALARMA';

export function alertaOsinerming(ventas: number, desvioPorcentaje: number): AlertaOsinerming {
  const desvio = Math.abs(toNumber(desvioPorcentaje));

  if (toNumber(ventas) < 3500 || desvio > 40) return 'CHEQUEAR!';
  if (desvio > 25) return 'AYUDA';

  return 'ALARMA';
}

export const ALERTA_CLASES: Record<AlertaOsinerming, string> = {
  'CHEQUEAR!': 'bg-rose-100 text-rose-700',
  AYUDA: 'bg-amber-100 text-amber-800',
  ALARMA: 'bg-ink-100 text-ink-600',
};

/** Porcentaje seguro (evita dividir entre cero). */
export function porcentaje(valor: number, base: number): number {
  const divisor = toNumber(base);

  if (divisor === 0) return 0;

  return (toNumber(valor) / divisor) * 100;
}

/** Clase reutilizable para las celdas numéricas de las tablas diarias. */
export const CELDA_NUMERO = cn('text-right font-mono text-xs tabular-nums');

/** Valor de `datetime-local` con la fecha y hora actuales (minutos). */
export function fechaHoraAhora(): string {
  const ahora = new Date();
  const dosDigitos = (valor: number) => String(valor).padStart(2, '0');

  return `${ahora.getFullYear()}-${dosDigitos(ahora.getMonth() + 1)}-${dosDigitos(ahora.getDate())}T${dosDigitos(ahora.getHours())}:${dosDigitos(ahora.getMinutes())}`;
}

/** Convierte el valor de `datetime-local` al formato que espera la API. */
export function aFechaHoraApi(valor: string): string {
  return valor.length === 16 ? `${valor.replace('T', ' ')}:00` : valor.replace('T', ' ');
}
